import React, { useState } from 'react';
import { Building2, MapPin, Phone, Mail, Clock, Plus, Edit, Trash2, Search, X, Save, User } from 'lucide-react';

const PharmacyManagement = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCity, setFilterCity] = useState('all');

  const [formData, setFormData] = useState({
    name: '',
    city: '',
    address: '',
    phone: '',
    email: '',
    pharmacist: '',
    openingHours: '',
    latitude: '',
    longitude: ''
  });

  const pharmacies = [
    {
      id: 1,
      name: 'Pharmacie Dakar Centre',
      city: 'Dakar',
      address: 'Avenue Léopold Sédar Senghor, Dakar',
      phone: '+221 77 123 45 67',
      email: 'contact@pharmaciedakar.sn',
      pharmacist: 'Dr. Fatou Ndiaye',
      openingHours: '8h - 20h',
      latitude: '14.6928',
      longitude: '-17.4467'
    },
    {
      id: 2,
      name: 'Pharmacie Thies Nord',
      city: 'Thies',
      address: 'Route Nationale 2, Thies',
      phone: '+221 77 234 56 78',
      email: 'pharmaciethies@gmail.com',
      pharmacist: 'Dr. Amadou Diop',
      openingHours: '8h - 21h',
      latitude: '14.7889',
      longitude: '-16.9322'
    },
    {
      id: 3,
      name: 'Pharmacie Mbour',
      city: 'Mbour',
      address: 'Centre Ville Mbour',
      phone: '+221 77 345 67 89',
      email: 'pharma.mbour@orange.sn',
      pharmacist: 'Dr. Aïssa Fall',
      openingHours: '7h30 - 22h',
      latitude: '14.4198',
      longitude: '-16.9619'
    },
    {
      id: 4,
      name: 'Pharmacie Saint-Louis',
      city: 'Saint-Louis',
      address: 'Rue de France, Saint-Louis',
      phone: '+221 77 456 78 90',
      email: 'pharma.stlouis@gmail.com',
      pharmacist: 'Dr. Moussa Sarr',
      openingHours: '8h - 19h',
      latitude: '16.0173',
      longitude: '-16.4889'
    },
    {
      id: 5,
      name: 'Pharmacie Rufisque',
      city: 'Rufisque',
      address: 'Avenue Blaise Diagne, Rufisque',
      phone: '+221 77 567 89 01',
      email: 'contact@pharmarufisque.sn',
      pharmacist: 'Dr. Khady Ba',
      openingHours: '8h - 20h',
      latitude: '14.7136',
      longitude: '-17.2694'
    }
  ];

  const cities = ['all', 'Dakar', 'Thies', 'Mbour', 'Saint-Louis', 'Rufisque', 'Kaolack', 'Ziguinchor', 'Louga', 'Diourbel'];

  const filteredPharmacies = pharmacies.filter(pharmacy => {
    const matchesSearch = pharmacy.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pharmacy.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pharmacy.pharmacist.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCity = filterCity === 'all' || pharmacy.city === filterCity;
    return matchesSearch && matchesCity;
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const openAddModal = () => {
    setFormData({
      name: '',
      city: '',
      address: '',
      phone: '',
      email: '',
      pharmacist: '',
      openingHours: '',
      latitude: '',
      longitude: ''
    });
    setShowAddModal(true);
  };

  const openEditModal = (pharmacy) => {
    setSelectedPharmacy(pharmacy);
    setFormData({
      name: pharmacy.name,
      city: pharmacy.city,
      address: pharmacy.address,
      phone: pharmacy.phone,
      email: pharmacy.email,
      pharmacist: pharmacy.pharmacist,
      openingHours: pharmacy.openingHours,
      latitude: pharmacy.latitude,
      longitude: pharmacy.longitude
    });
  };

  const closeModal = () => {
    setShowAddModal(false);
    setSelectedPharmacy(null);
  };

  const handleSubmit = () => {
    alert('Fonctionnalité pas encore disponible');
    closeModal();
  };

  const handleDelete = (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette pharmacie ?')) {
      alert('Fonctionnalité pas encore disponible');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="fixed inset-0 overflow-hidden pointer-events-none opacity-30">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgb(209, 213, 219) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <main className="relative z-10 p-4 md:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-4xl font-black text-gray-800 mb-4">
            Gestion des Pharmacies
          </h1>
          <p className="text-gray-600 text-lg">Gérez toutes les pharmacies du réseau</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total</p>
                <p className="text-3xl font-bold text-gray-800">{pharmacies.length}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-md">
                <Building2 className="text-white" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Villes</p>
                <p className="text-3xl font-bold text-gray-800">{cities.length - 1}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md">
                <MapPin className="text-white" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Ouvertes</p>
                <p className="text-3xl font-bold text-emerald-600">{pharmacies.length}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-md">
                <Clock className="text-white" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Pharmaciens</p>
                <p className="text-3xl font-bold text-gray-800">{pharmacies.length}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-md">
                <User className="text-white" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-200">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex-1 w-full md:w-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Rechercher une pharmacie..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex gap-3 w-full md:w-auto">
              <select
                value={filterCity}
                onChange={(e) => setFilterCity(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                {cities.map(city => (
                  <option key={city} value={city}>
                    {city === 'all' ? '🌍 Toutes les villes' : city}
                  </option>
                ))}
              </select>

              <button
                onClick={openAddModal}
                className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:shadow-xl transition-all font-bold whitespace-nowrap"
              >
                <Plus size={20} />
                <span>Ajouter</span>
              </button>
            </div>
          </div>
        </div>

        {/* Pharmacies Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPharmacies.map((pharmacy) => (
            <div key={pharmacy.id} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all border border-gray-200 overflow-hidden group">
              <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                      <Building2 className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-lg">{pharmacy.name}</h3>
                      <p className="text-white/80 text-sm flex items-center">
                        <MapPin size={14} className="mr-1" />
                        {pharmacy.city}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-3">
                <div className="flex items-start space-x-3">
                  <MapPin size={18} className="text-gray-400 flex-shrink-0 mt-1" />
                  <p className="text-gray-700 text-sm">{pharmacy.address}</p>
                </div>

                <div className="flex items-center space-x-3">
                  <Phone size={18} className="text-gray-400 flex-shrink-0" />
                  <a href={`tel:${pharmacy.phone}`} className="text-gray-700 text-sm hover:text-emerald-600 transition-colors">
                    {pharmacy.phone}
                  </a>
                </div>

                <div className="flex items-center space-x-3">
                  <Mail size={18} className="text-gray-400 flex-shrink-0" />
                  <a href={`mailto:${pharmacy.email}`} className="text-gray-700 text-sm hover:text-emerald-600 transition-colors truncate">
                    {pharmacy.email}
                  </a>
                </div>

                <div className="flex items-center space-x-3">
                  <User size={18} className="text-gray-400 flex-shrink-0" />
                  <p className="text-gray-700 text-sm font-medium">{pharmacy.pharmacist}</p>
                </div>

                <div className="flex items-center space-x-3">
                  <Clock size={18} className="text-gray-400 flex-shrink-0" />
                  <p className="text-gray-700 text-sm">{pharmacy.openingHours}</p>
                </div>

                <div className="pt-3 border-t border-gray-200 flex space-x-2">
                  <button
                    onClick={() => openEditModal(pharmacy)}
                    className="flex-1 px-4 py-2 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-all font-semibold text-sm flex items-center justify-center"
                  >
                    <Edit size={16} className="mr-2" />
                    Modifier
                  </button>
                  <button
                    onClick={() => handleDelete(pharmacy.id)}
                    className="px-4 py-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-all"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredPharmacies.length === 0 && (
          <div className="text-center py-12">
            <Building2 size={64} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg">Aucune pharmacie trouvée</p>
          </div>
        )}
      </main>

      {/* Add/Edit Modal */}
      {(showAddModal || selectedPharmacy) && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-gray-200">
            <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 flex items-center justify-between z-10 rounded-t-3xl">
              <h2 className="text-2xl font-black text-white">
                {selectedPharmacy ? '✏️ Modifier la pharmacie' : '➕ Nouvelle pharmacie'}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-white/20 rounded-xl transition-all text-white"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Nom de la pharmacie *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="Pharmacie..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Ville *
                    </label>
                    <select
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="">Sélectionner...</option>
                      {cities.filter(c => c !== 'all').map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Adresse complète *
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Avenue..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="+221 77 123 45 67"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="contact@pharmacie.sn"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Pharmacien responsable *
                    </label>
                    <input
                      type="text"
                      name="pharmacist"
                      value={formData.pharmacist}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="Dr. Nom Prénom"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Horaires d'ouverture
                    </label>
                    <input
                      type="text"
                      name="openingHours"
                      value={formData.openingHours}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="8h - 20h"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Latitude
                    </label>
                    <input
                      type="text"
                      name="latitude"
                      value={formData.latitude}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="14.6928"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Longitude
                    </label>
                    <input
                      type="text"
                      name="longitude"
                      value={formData.longitude}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="-17.4467"
                    />
                  </div>
                </div>
              </div>

              <div className="flex space-x-3 pt-6 border-t border-gray-200 mt-6">
                <button
                  onClick={closeModal}
                  className="flex-1 px-6 py-3 bg-gray-100 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-200 transition-all font-bold"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSubmit}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:shadow-xl transition-all font-bold"
                >
                  <Save size={20} className="inline mr-2" />
                  {selectedPharmacy ? 'Mettre à jour' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PharmacyManagement;